import React, { useContext, useEffect, useState, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import supabase from "../Config/Supabase";
import DataContext from "../Context/DAtaContext";
import { toast } from "react-toastify";
import { BsFillTrash3Fill } from "react-icons/bs";
import UsersContext from "../Context/UsersContext";
import {
  Loader,
  Search,
  UserCheck,
  X,
  ChevronDown,
  MapPin,
  Plus,
  User,
} from "lucide-react";
import {
  saveFarmerOffline,
  syncAllOfflineData,
  getAllOfflineFarmers,
} from "../local database/OfflineUtils";

const { v4: uuidv4 } = require("uuid");

/* ─────────────────────────────────────────────
   Small reusable field-error component
───────────────────────────────────────────── */
const FieldError = ({ show, message }) =>
  show && message ? (
    <motion.p
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex items-center gap-1 mt-1 text-xs text-red-400"
    >
      <span className="inline-block w-1 h-1 bg-red-400 rounded-full" />
      {message}
    </motion.p>
  ) : null;

/* ─────────────────────────────────────────────
   Labelled field wrapper
───────────────────────────────────────────── */
const Field = ({ label, children }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-xs font-semibold tracking-widest text-gray-400 uppercase dark:text-gray-500">
      {label}
    </label>
    {children}
  </div>
);

const inputCls =
  "w-full px-3 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm text-gray-800 dark:text-gray-200 outline-none transition focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 placeholder:text-gray-400";

const selectCls = inputCls + " appearance-none cursor-pointer";

/* ─────────────────────────────────────────────
   Section layout helpers
───────────────────────────────────────────── */
const accentBorder = {
  emerald: "border-emerald-500",
  blue: "border-blue-500",
  violet: "border-violet-500",
  amber: "border-amber-500",
};

const SectionHeader = ({ title, accent = "emerald", icon }) => (
  <div className={`flex items-center gap-2 mb-4  ${accentBorder[accent]}`}>
    {icon && <span className="text-gray-400">{icon}</span>}
    <h2 className="text-sm font-bold tracking-wide text-gray-700 uppercase dark:text-gray-200">
      {title}
    </h2>
  </div>
);

const Section = ({ title, icon, accent = "emerald", children }) => (
  <div className="mb-8 ">
    <SectionHeader title={title} icon={icon} accent={accent} />
    <div className="grid grid-cols-1 gap-5 p-5 bg-white border border-gray-200 shadow-sm dark:bg-gray-900 dark:border-gray-800 rounded-2xl md:grid-cols-2 xl:grid-cols-3">
      {children}
    </div>
  </div>
);

/* ─────────────────────────────────────────────
   Main Component
───────────────────────────────────────────── */
const FarmerRegister = ({ farmers, FamaId, creator_id, creator_village }) => {
  const [offlineFarmer, setOfflineFarmers] = useState([]);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // ── Re-registration search state ──────────────────────────
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedExistingFarmer, setSelectedExistingFarmer] = useState(null);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const searchRef = useRef(null);

  const { vegetableBundle, Villages } = useContext(DataContext);
  const { fetchFarmerBundle, farmerlist } = useContext(DataContext);
  const { fetchFarmers } = useContext(UsersContext);

  // Zone-filtered villages
  const userVillage = Villages?.find((v) => v?.id === creator_village);
  const zone_id = userVillage?.zone_id;
  const zoneVillages = zone_id
    ? Villages?.filter((v) => v?.zone_id === zone_id)
    : (Villages ?? []);

  const getFarmerId = () => {
    if (isOffline && offlineFarmer?.id) return offlineFarmer.id + 1;
    const latest = farmers?.sort(
      (a, b) => new Date(b?.created_at) - new Date(a?.created_at),
    )[0];
    return latest?.id + 1 || FamaId + 1 || 1;
  };

  const blankFarmer = () => ({
    id: getFarmerId(),
    uuid: uuidv4(),
    name: "",
    gender: "male",
    village_id: "",
    longitude: "",
    latitude: "",
    phone: "+255",
    belongs_in_group: false,
    number_of_people_in_house_hold: "",
    number_of_children_under_5: "",
    group_membership_status: "",
    number_of_breast_feeding_children: "",
    group_name: "",
    creator_id: creator_id,
    prefered_bundle: "",
    device_id: uuidv4(),
    created_at: new Date().toISOString(),
  });

  const [farmerData, setFarmerData] = useState(blankFarmer);

  /* ── Re-registration search logic ────────────────────────── */
  useEffect(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q || q.length < 2) {
      setSearchResults([]);
      setShowSearchDropdown(false);
      return;
    }
    const filtered = (farmerlist || []).filter(
      (f) =>
        f?.name?.toLowerCase().includes(q) ||
        f?.phone?.toLowerCase().includes(q),
    );
    setSearchResults(filtered.slice(0, 8));
    setShowSearchDropdown(filtered.length > 0);
  }, [searchQuery, farmerlist]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSearchDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelectExistingFarmer = (farmer) => {
    setSelectedExistingFarmer(farmer);
    setShowSearchDropdown(false);
    setSearchQuery(farmer.name);

    // Pre-fill form — intentionally omit prefered_bundle (agent assigns new one)
    setFarmerData((prev) => ({
      ...prev,
      name: farmer.name || "",
      gender: farmer.gender || "male",
      village_id: farmer.village_id || "",
      phone: farmer.phone || "+255",
      belongs_in_group: farmer.belongs_in_group || false,
      number_of_people_in_house_hold:
        farmer.number_of_people_in_house_hold || "",
      number_of_children_under_5: farmer.number_of_children_under_5 || "",
      group_membership_status: farmer.group_membership_status || "",
      number_of_breast_feeding_children:
        farmer.number_of_breast_feeding_children || "",
      group_name: farmer.group_name || "",
      // uuid, id, device_id remain fresh for new record
    }));
    setErrors({});
    setTouched({});
  };

  const clearExistingFarmer = () => {
    setSelectedExistingFarmer(null);
    setSearchQuery("");
    setSearchResults([]);
  };

  /* ── Validation ─────────────────────────────────────────── */
  const validateField = (name, value, allData = farmerData) => {
    switch (name) {
      case "name":
        if (!value || value.trim().length < 2)
          return "Full name must be at least 2 characters";
        if (!/^[a-zA-Z\s]+$/.test(value))
          return "Name should only contain letters and spaces";
        return "";
      case "village_id":
        return !value ? "Please select a village" : "";
      case "phone":
        if (!value || value.length < 10)
          return "Please enter a valid phone number";
        if (!value || value.length >= 14)
          return "Please enter a valid phone number";
        if (!value.startsWith("+255")) return "Phone must start with +255";
        return "";
      case "number_of_people_in_house_hold":
        if (!value || parseInt(value) < 1) return "Must be at least 1";
        if (parseInt(value) > 50) return "Number seems too high, please verify";
        return "";
      case "number_of_children_under_5":
        if (value && parseInt(value) < 0) return "Cannot be negative";
        if (
          value &&
          allData.number_of_people_in_house_hold &&
          parseInt(value) > parseInt(allData.number_of_people_in_house_hold)
        )
          return "Cannot exceed total household members";
        return "";
      case "number_of_breast_feeding_children":
        if (value && parseInt(value) < 0) return "Cannot be negative";
        if (
          value &&
          allData.number_of_children_under_5 &&
          parseInt(value) > parseInt(allData.number_of_children_under_5)
        )
          return "Cannot exceed children under 5";
        return "";
      case "group_name":
        if (allData.belongs_in_group && (!value || value.trim().length < 2))
          return "Group name required (min 2 characters)";
        return "";
      case "group_membership_status":
        if (allData.belongs_in_group && !value)
          return "Please select a group position";
        return "";
      default:
        return "";
    }
  };

  const validateBundles = () => {
    const errs = [];
    if (rows.length === 0) {
      errs.push("At least one bundle must be selected");
      return errs;
    }
    rows.forEach((row, i) => {
      if (!row.kifurushi || !row.bundle_id)
        errs.push(`Bundle ${i + 1}: Please select a bundle`);
      if (!row.amount || row.amount < 1)
        errs.push(`Bundle ${i + 1}: Amount must be at least 1`);
      if (row.amount > 100) errs.push(`Bundle ${i + 1}: Amount seems too high`);
    });
    return errs;
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(farmerData).forEach((key) => {
      const e = validateField(key, farmerData[key], farmerData);
      if (e) newErrors[key] = e;
    });
    const bundleErrors = validateBundles();
    if (bundleErrors.length > 0) newErrors.bundles = bundleErrors;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ── Offline ─────────────────────────────────────────────── */
  useEffect(() => {
    loadOfflineFarmers();
  }, []);

  useEffect(() => {
    setFarmerData((prev) => ({ ...prev, id: getFarmerId() }));
  }, [isOffline, offlineFarmer, farmers, FamaId]);

  const loadOfflineFarmers = async () => {
    try {
      const list = await getAllOfflineFarmers();
      const latest = list?.sort(
        (a, b) => new Date(b?.created_at) - new Date(a?.created_at),
      )[0];
      setOfflineFarmers(latest || null);
    } catch {
      toast.error("Failed to load offline farmers");
    }
  };

  useEffect(() => {
    const handleOnline = async () => {
      setIsOffline(false);
      toast.info("Back online! Attempting to sync offline data…");
      try {
        const result = await syncAllOfflineData();
        if (result.synced > 0) {
          toast.success(`Successfully synced ${result.synced} farmer(s)`);
          fetchFarmers();
          await loadOfflineFarmers();
        }
      } catch {
        toast.error("Auto-sync failed. You can manually sync later.");
      }
    };
    const handleOffline = () => {
      setIsOffline(true);
      toast.warning("You are offline. Data will be saved locally.");
    };
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [fetchFarmers]);

  /* ── Form handlers ───────────────────────────────────────── */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    setFarmerData({ ...farmerData, [name]: newValue });
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, newValue, {
      ...farmerData,
      [name]: newValue,
    });
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  // Phone handler — enforces +255 prefix
  const handlePhoneChange = (e) => {
    let val = e.target.value;
    if (!val.startsWith("+255")) val = "+255" + val.replace(/^\+?255?/, "");
    setFarmerData({ ...farmerData, phone: val });
    setTouched((prev) => ({ ...prev, phone: true }));
    const error = validateField("phone", val, { ...farmerData, phone: val });
    setErrors((prev) => ({ ...prev, phone: error }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fix all validation errors before submitting");
      const allTouched = {};
      Object.keys(farmerData).forEach((f) => (allTouched[f] = true));
      setTouched(allTouched);
      return;
    }
    setLoading(true);

    if (isOffline) {
      try {
        const bundleData = rows.map((row) => ({
          id: row.id,
          amount: row.amount,
          farmer_id: farmerData.uuid,
          bundle_id: row.bundle_id,
          device_id: row.device_id,
          created_at: new Date().toISOString(),
        }));
        await saveFarmerOffline(farmerData, bundleData);
        toast.success(
          "Farmer data saved offline. It will be synced when online.",
        );
        await loadOfflineFarmers();
        resetForm();
      } catch (err) {
        toast.error("Failed to save data offline: " + err.message);
      }
      setLoading(false);
    } else {
      try {
        const { data: farmerDataResponse, error } = await supabase
          .from("farmer")
          .insert([
            {
              id: farmerData.id,
              uuid: farmerData.uuid,
              name: farmerData.name,
              gender: farmerData.gender,
              village_id: farmerData.village_id,
              longitude: farmerData.longitude,
              latitude: farmerData.latitude,
              phone: farmerData.phone,
              belongs_in_group: farmerData.belongs_in_group,
              number_of_people_in_house_hold:
                +farmerData.number_of_people_in_house_hold,
              number_of_children_under_5:
                +farmerData.number_of_children_under_5,
              group_membership_status: farmerData.group_membership_status,
              number_of_breast_feeding_children:
                +farmerData.number_of_breast_feeding_children,
              group_name: farmerData.group_name,
              creator_id: farmerData.creator_id,
              device_id: farmerData.device_id,
              prefered_bundle: farmerData.prefered_bundle,
              created_at: new Date().toISOString(),
            },
          ])
          .select();

        if (error) throw error;
        await handleBundleSubmit(farmerDataResponse[0].uuid);
        fetchFarmers();
        toast.success("Farmer enrolled successfully!");
        resetForm();
        // clearExistingFarmer();
      } catch (err) {
        toast.error("Failed to enroll farmer: " + err.message);
      }
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFarmerData(blankFarmer());
    setSelectedExistingFarmer(null);
    setSearchQuery("");
    setSearchResults([]);
    setShowSearchDropdown(false);
    setErrors({});
    setTouched({});
    setRows([
      {
        id: uuidv4(),
        amount: 1,
        bundle_id: "",
        kifurushi: "",
        vilivyomo: "",
        farmer_id: farmerData?.uuid,
        device_id: farmerData?.device_id,
      },
    ]);
  };

  const getGPSLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by this browser.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        setFarmerData({
          ...farmerData,
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        }),
      (err) => toast.error("Failed to get GPS location: " + err.message),
    );
  };

  /* ── Bundle rows ─────────────────────────────────────────── */
  const [rows, setRows] = useState([
    {
      id: uuidv4(),
      amount: 1,
      bundle_id: "",
      kifurushi: "",
      vilivyomo: "",
      farmer_id: farmerData?.uuid,
      device_id: farmerData?.device_id,
    },
  ]);

  const addRow = () =>
    setRows([
      ...rows,
      {
        id: uuidv4(),
        amount: 1,
        kifurushi: "",
        vilivyomo: "",
        bundle_id: "",
        farmer_id: farmerData?.uuid,
        device_id: farmerData?.device_id,
      },
    ]);

  const deleteRow = (id) => {
    if (rows.length > 1) setRows(rows.filter((r) => r.id !== id));
    else toast.warning("At least one bundle must be selected");
  };

  useEffect(() => {
    if (rows.length > 0) {
      const info = rows.map((d) =>
        `${d.amount} ${d.kifurushi} ${d.vilivyomo || ""}`.trim(),
      );
      setFarmerData((prev) => ({ ...prev, prefered_bundle: info.join(", ") }));
    }
  }, [rows]);

  const handleBundleSubmit = async (farmerUuid) => {
    try {
      const dataToInsert = rows.map((r) => ({
        id: r.id,
        amount: r.amount,
        farmer_id: farmerUuid,
        bundle_id: r.bundle_id,
        device_id: r.device_id,
        created_at: new Date().toISOString(),
      }));
      const { error } = await supabase
        .from("farmer_bundle")
        .insert(dataToInsert)
        .select();
      if (error)
        throw new Error(
          error.message || "Unknown error occurred while enrolling bundle.",
        );
      fetchFarmerBundle();
      toast.success("Bundle enrolled successfully!");
    } catch (err) {
      console.error("Error enrolling bundle:", err.message || err);
      toast.error("Failed to enroll bundle.");
    }
  };

  /* ── UI ──────────────────────────────────────────────────── */
  return (
    <motion.div
      className="py-4 "
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      {/* Offline banner */}
      <AnimatePresence>
        {isOffline && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="flex items-center gap-3 px-4 py-3 mx-auto mb-5 text-sm font-medium border md:container rounded-xl bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-400"
          >
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse shrink-0" />
            You are offline, data will be saved locally and synced automatically
            when reconnected.
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mx-auto md:container">
        {/* Page header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <p className="mb-1 text-xs font-semibold tracking-widest uppercase text-emerald-500">
              New Era Enrollment
            </p>
            <h1 className="text-xl font-bold tracking-tight text-gray-900 md:text-2xl dark:text-white">
              Register Farmer
            </h1>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-gray-200 dark:border-gray-700 text-xs bg-white dark:bg-gray-900 shrink-0">
            <span className="text-gray-400">ID</span>
            <span className="font-bold text-gray-700 dark:text-gray-200">
              #{farmerData.id}
            </span>
          </div>
        </div>

        {/* ══════════════════════════════════════════
            SECTION 1 — Re-register existing farmer
        ══════════════════════════════════════════ */}
        <Section
          title="Re-register Existing Farmer"
          icon={<UserCheck size={15} />}
          accent="emerald"
        >
          <div className="col-span-full">
            <p className="mb-3 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
              Search by <strong>name</strong> or <strong>phone number</strong>{" "}
              to pre-fill a previously registered farmer's details. The
              preferred bundle will be left blank, Assign a new one for this
              era.
            </p>
            <div className="relative" ref={searchRef}>
              <div className="relative">
                <Search
                  size={14}
                  className="absolute text-gray-400 -translate-y-1/2 pointer-events-none left-3 top-1/2"
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() =>
                    searchResults.length > 0 && setShowSearchDropdown(true)
                  }
                  placeholder="Search farmer by name or phone…"
                  className={`${inputCls} pl-9 pr-9`}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={clearExistingFarmer}
                    className="absolute text-gray-400 -translate-y-1/2 right-3 top-1/2 hover:text-gray-600"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>

              {/* Search dropdown */}
              <AnimatePresence>
                {showSearchDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 6 }}
                    className="absolute z-50 mt-1.5 w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl overflow-hidden"
                  >
                    {searchResults.map((f) => (
                      <button
                        key={f.uuid || f.id}
                        type="button"
                        onClick={() => handleSelectExistingFarmer(f)}
                        className="flex items-center w-full gap-3 px-4 py-3 text-left transition border-b border-gray-100 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 dark:border-gray-800 last:border-0"
                      >
                        <div className="flex items-center justify-center w-8 h-8 text-xs font-bold rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 shrink-0">
                          {f.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-800 truncate dark:text-gray-100">
                            {f.name}
                          </p>
                          <p className="text-xs text-gray-400 truncate">
                            {f.phone} · {f.gender}
                          </p>
                        </div>
                        <UserCheck
                          size={13}
                          className="text-emerald-500 shrink-0"
                        />
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Selected farmer banner */}
            <AnimatePresence>
              {selectedExistingFarmer && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex items-center gap-3 px-4 py-3 mt-3 overflow-hidden border rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800"
                >
                  <div className="flex items-center justify-center text-sm font-bold text-white rounded-full w-9 h-9 bg-emerald-500 shrink-0">
                    {selectedExistingFarmer.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-emerald-800 dark:text-emerald-300">
                      {selectedExistingFarmer.name}
                    </p>
                    <p className="text-xs text-emerald-600 dark:text-emerald-500">
                      Info pre-filled from previous era — assign a new bundle
                      below.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={clearExistingFarmer}
                    className="transition text-emerald-400 hover:text-emerald-600 shrink-0"
                  >
                    <X size={15} />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Section>

        {/* ══════════════════════════════════════════
            SECTION 2 — Personal Information
        ══════════════════════════════════════════ */}
        <Section
          title="Personal Information"
          icon={<User size={15} />}
          accent="blue"
        >
          {/* Full Name */}
          <Field label="Full Name (Jina Kamili)">
            <input
              type="text"
              name="name"
              value={farmerData.name}
              onChange={handleChange}
              placeholder="e.g. Mhando Zuberi"
              className={inputCls}
            />
            <FieldError show={touched.name} message={errors.name} />
          </Field>

          {/* Gender */}
          <Field label="Gender (Jinsia)">
            <div className="relative">
              <select
                name="gender"
                value={farmerData.gender}
                onChange={handleChange}
                className={selectCls}
              >
                <option value="male">Male (Mwanaume)</option>
                <option value="female">Female (Mwanamke)</option>
              </select>
              <ChevronDown
                size={13}
                className="absolute text-gray-400 -translate-y-1/2 pointer-events-none right-3 top-1/2"
              />
            </div>
          </Field>

          {/* Village */}
          <Field label="Village (Kijiji)">
            <div className="relative">
              <select
                name="village_id"
                value={farmerData.village_id}
                onChange={handleChange}
                className={selectCls}
              >
                <option value="" disabled>
                  Select Village
                </option>
                {zoneVillages?.map((v) => (
                  <option key={v?.id} value={v?.id}>
                    {v?.name}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={13}
                className="absolute text-gray-400 -translate-y-1/2 pointer-events-none right-3 top-1/2"
              />
            </div>
            <FieldError show={touched.village_id} message={errors.village_id} />
          </Field>

          {/* Phone Number — custom input with +255 prefix */}
          <Field label="Phone Number (Namba ya Simu)">
            <div className="relative">
              <span className="absolute text-lg text-black -translate-y-1/2 pointer-events-none select-none dark:text-gray-200 left-3 top-1/2">
                🇹🇿
              </span>
              <input
                type="tel"
                name="phone"
                value={farmerData.phone}
                onChange={handlePhoneChange}
                placeholder="+255 7XX XXX XXX"
                className={`${inputCls} pl-9`}
              />
            </div>
            <FieldError show={touched.phone} message={errors.phone} />
          </Field>

          {/* GPS Location */}
          <Field label="GPS Location">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={getGPSLocation}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-green-600 hover:bg-green-500 text-white text-sm font-medium transition shrink-0"
              >
                <MapPin size={14} />
                Allow GPS
              </button>
              {farmerData.latitude && (
                <span className="text-xs text-gray-500 truncate dark:text-gray-400">
                  {Number(farmerData.latitude).toFixed(5)},{" "}
                  {Number(farmerData.longitude).toFixed(5)}
                </span>
              )}
            </div>
          </Field>
        </Section>

        {/* ══════════════════════════════════════════
            SECTION 3 — Group Membership
        ══════════════════════════════════════════ */}
        <Section title="Group Membership" icon={null} accent="violet">
          <div className="col-span-full">
            <label className="flex items-center gap-3 cursor-pointer select-none w-fit">
              <div className="relative">
                <input
                  type="checkbox"
                  name="belongs_in_group"
                  checked={farmerData.belongs_in_group}
                  onChange={handleChange}
                  className="sr-only peer"
                />
                <div className="w-10 h-5 transition-colors bg-gray-200 rounded-full dark:bg-gray-700 peer-checked:bg-emerald-500" />
                <div className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5" />
              </div>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Farmer belongs to a group{" "}
                <span className="text-xs text-gray-400">
                  (Yupo kwenye Kikundi?)
                </span>
              </span>
            </label>
          </div>

          <AnimatePresence>
            {farmerData.belongs_in_group && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="grid grid-cols-1 gap-5 overflow-hidden col-span-full md:grid-cols-2"
              >
                <Field label="Group Name (Jina la Kikundi)">
                  <input
                    type="text"
                    name="group_name"
                    value={farmerData.group_name}
                    onChange={handleChange}
                    placeholder="e.g. Kilimo Kwanza"
                    className={inputCls}
                  />
                  <FieldError
                    show={touched.group_name}
                    message={errors.group_name}
                  />
                </Field>

                <Field label="Group Position (Cheo chake kwenye kikundi)">
                  <div className="relative">
                    <select
                      name="group_membership_status"
                      value={farmerData.group_membership_status}
                      onChange={handleChange}
                      className={selectCls}
                    >
                      <option value="">Select Position</option>
                      <option value="mwenyekiti">Mwenyekiti</option>
                      <option value="katibu">Katibu</option>
                      <option value="mweka Hazina">Mweka Hazina</option>
                      <option value="mwana Chama">Mwana Chama</option>
                    </select>
                    <ChevronDown
                      size={13}
                      className="absolute text-gray-400 -translate-y-1/2 pointer-events-none right-3 top-1/2"
                    />
                  </div>
                  <FieldError
                    show={touched.group_membership_status}
                    message={errors.group_membership_status}
                  />
                </Field>
              </motion.div>
            )}
          </AnimatePresence>
        </Section>

        {/* ══════════════════════════════════════════
            SECTION 4 — Household Information
        ══════════════════════════════════════════ */}
        <Section title="Household Information" icon={null} accent="amber">
          <Field label="Total People in Household (Idadi ya watu unao ishi nao)">
            <input
              type="number"
              name="number_of_people_in_house_hold"
              value={farmerData.number_of_people_in_house_hold}
              onChange={handleChange}
              placeholder="e.g. 5"
              min={1}
              className={inputCls}
            />
            <FieldError
              show={touched.number_of_people_in_house_hold}
              message={errors.number_of_people_in_house_hold}
            />
          </Field>

          <Field label="Children Under 5 (Watoto chini ya miaka 5)">
            <input
              type="number"
              name="number_of_children_under_5"
              value={farmerData.number_of_children_under_5}
              onChange={handleChange}
              placeholder="e.g. 2"
              min={0}
              className={inputCls}
            />
            <FieldError
              show={touched.number_of_children_under_5}
              message={errors.number_of_children_under_5}
            />
          </Field>

          <Field label="Breastfeeding Children (Watoto wanao nyonya)">
            <input
              type="number"
              name="number_of_breast_feeding_children"
              value={farmerData.number_of_breast_feeding_children}
              onChange={handleChange}
              placeholder="e.g. 1"
              min={0}
              className={inputCls}
            />
            <FieldError
              show={touched.number_of_breast_feeding_children}
              message={errors.number_of_breast_feeding_children}
            />
          </Field>
        </Section>

        {/* ══════════════════════════════════════════
            SECTION 5 — Bundles (Vifurushi)
        ══════════════════════════════════════════ */}
        <div className="mb-8">
          <SectionHeader title="Vifurushi (Bundles)" accent="emerald" />
          <div className="overflow-hidden bg-white border border-gray-200 shadow-sm dark:bg-gray-900 dark:border-gray-800 rounded-2xl">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs tracking-wider text-gray-500 uppercase bg-gray-50 dark:bg-gray-800/60 dark:text-gray-400">
                    <th className="hidden w-10 px-4 py-3 text-left md:table-cell">
                      #
                    </th>
                    <th className="px-4 py-3 text-left">Bundle (Kifurushi)</th>
                    <th className="px-4 py-3 text-left w-28">Qty</th>
                    <th className="hidden px-4 py-3 text-left md:table-cell">
                      Contents (Vilivyomo)
                    </th>
                    <th className="px-4 py-3 text-left w-14"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  <AnimatePresence>
                    {rows.map((row, index) => (
                      <motion.tr
                        key={row.id}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.25 }}
                        className="hover:bg-gray-50/80 dark:hover:bg-gray-800/30"
                      >
                        <td className="hidden px-4 py-3 font-mono text-xs text-gray-400 md:table-cell">
                          {index + 1}
                        </td>

                        <td className="px-4 py-3">
                          <div className="relative">
                            <select
                              value={row.kifurushi || ""}
                              onChange={(e) => {
                                const sel = vegetableBundle?.find(
                                  (b) => b.name === e.target.value,
                                );
                                const newRows = [...rows];
                                newRows[index] = {
                                  ...newRows[index],
                                  kifurushi: e.target.value,
                                  bundle_id: sel?.id || "",
                                  vilivyomo: sel?.sub_bundles || "",
                                };
                                setRows(newRows);
                              }}
                              className={`${selectCls} pr-8 min-w-[160px]`}
                            >
                              <option value="">Select bundle…</option>
                              {vegetableBundle?.map((b) => (
                                <option key={b.id} value={b.name}>
                                  {b.name}
                                </option>
                              ))}
                            </select>
                            <ChevronDown
                              size={12}
                              className="absolute text-gray-400 -translate-y-1/2 pointer-events-none right-2 top-1/2"
                            />
                          </div>
                        </td>

                        <td className="px-1 py-3">
                          <input
                            type="number"
                            min={1}
                            value={row.amount}
                            onChange={(e) => {
                              const newRows = [...rows];
                              newRows[index].amount = parseInt(e.target.value);
                              setRows(newRows);
                            }}
                            className={`${inputCls} w-20`}
                          />
                        </td>

                        <td className="hidden px-4 py-3 md:table-cell">
                          <input
                            type="text"
                            readOnly
                            value={row.vilivyomo || ""}
                            placeholder="Auto-filled"
                            className={`${inputCls} bg-gray-50 dark:bg-gray-800/50 cursor-default text-gray-500 dark:text-gray-400`}
                          />
                        </td>

                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => deleteRow(row.id)}
                            className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition"
                          >
                            <BsFillTrash3Fill size={13} />
                          </button>
                        </td>
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>

            <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-800">
              <button
                type="button"
                onClick={addRow}
                className="flex items-center gap-2 text-sm font-medium transition text-emerald-600 dark:text-emerald-400 hover:text-emerald-700"
              >
                <Plus size={14} />
                Ongeza Bando
              </button>
            </div>
          </div>

          {/* Bundle errors */}
          {errors.bundles && (
            <div className="mt-2 space-y-1">
              {errors.bundles.map((err, i) => (
                <p
                  key={i}
                  className="flex items-center gap-1 text-xs text-red-400"
                >
                  <span className="inline-block w-1 h-1 bg-red-400 rounded-full" />
                  {err}
                </p>
              ))}
            </div>
          )}
        </div>

        {/* Submit button */}
        <div className="flex justify-end">
          <motion.button
            type="button"
            onClick={handleSubmit}
            whileTap={{ scale: 0.97 }}
            disabled={loading}
            className={`flex items-center gap-2 px-8 py-3 rounded-xl text-sm font-semibold text-white shadow-md transition ${
              loading
                ? "bg-gray-300 dark:bg-gray-700 cursor-not-allowed shadow-none"
                : "bg-green-600 hover:bg-green-700 shadow-green-200 dark:shadow-green-900/30"
            }`}
          >
            {loading ? (
              <>
                <Loader
                  size={15}
                  className="animate-spin [animation-duration:0.6s]"
                />
                Registering…
              </>
            ) : (
              <>
                <UserCheck size={15} />
                Register Farmer
              </>
            )}
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

export default FarmerRegister;
